import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, RefreshCw, AlertCircle, Shield, AlertTriangle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function AzureIntegrationPage() {
  const { user, viewingOrg } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [connectedAt, setConnectedAt] = useState(null);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const [subscriptionId, setSubscriptionId] = useState('');
  const [securityData, setSecurityData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    checkAzureStatus();
  }, [viewingOrg]);

  const checkAzureStatus = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/azure/status${params}`);
      setConfigured(response.data.configured || false);
      setConnected(response.data.connected || false);
      setConnectedAt(response.data.connected_at);
      setLastSync(response.data.last_sync);
      setConnectedAccount({
        email: response.data.connected_email,
        domain: response.data.connected_domain,
        organization: response.data.connected_organization
      });
      
      if (response.data.connected) {
        fetchDefenderPosture();
      } else {
        setSecurityData(null);
      }
    } catch (error) {
      console.error('Failed to check Azure status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefenderPosture = async () => {
    try {
      // Add view_tenant_id param if super admin is viewing a tenant
      const params = isSuperAdmin && viewingOrg?.id ? `?view_tenant_id=${viewingOrg.id}` : '';
      const response = await axios.get(`${API}/azure/defender-posture${params}`);
      setSecurityData(response.data);
      setSubscriptionId(response.data.subscription_id || '');
    } catch (error) {
      console.error('Failed to fetch Defender for Cloud data:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      }
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/azure/sync-controls`);
      toast.success(`Successfully synced! Updated ${response.data.controls_updated} controls.`);
      fetchDefenderPosture();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync controls');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to clear Azure sync history? Note: Microsoft OAuth connection will remain intact.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/azure/disconnect`);
      toast.success('Azure sync history cleared');
      setSecurityData(null);
      setLastSync(null);
    } catch (error) {
      toast.error('Failed to disconnect Azure');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="azure-integration-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-semibold tracking-tight text-slate-900">
          Microsoft Defender for Cloud Integration
        </h1>
        <p className="text-slate-600 mt-2">
          Connect your Azure subscription to automatically assess ISO 27001 and SOC 2 controls based on Defender for Cloud recommendations
        </p>
      </div>

      {/* Microsoft OAuth Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Azure Defender uses Microsoft OAuth</p>
            <p>
              Azure Defender for Cloud shares authentication with Microsoft 365. 
              {!configured && (
                <>
                  {' '}To get started, first{' '}
                  <a href="/m365-settings" className="text-blue-700 hover:underline font-medium">
                    configure Microsoft 365 integration
                  </a>
                  {' '}and sign in with a Microsoft account that has access to your Azure subscription.
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Connection Status */}
      <div className="border border-border rounded-sm bg-white p-6">
        {/* Connected Account Info Banner */}
        {connected && connectedAccount && (connectedAccount.email || connectedAccount.organization) && (
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0078D4] rounded-full flex items-center justify-center">
                <span className="text-white font-semibold text-sm">
                  {connectedAccount.email?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-slate-900">Connected to Azure Defender</span>
                </div>
                <p className="text-sm text-slate-600">
                  {connectedAccount.organization && <span className="font-medium">{connectedAccount.organization}</span>}
                  {connectedAccount.organization && connectedAccount.domain && <span> · </span>}
                  {connectedAccount.domain && <span>{connectedAccount.domain}</span>}
                  {!connectedAccount.organization && !connectedAccount.domain && connectedAccount.email && (
                    <span>{connectedAccount.email}</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {connected ? (
              <CheckCircle className="w-6 h-6 text-green-600" />
            ) : configured ? (
              <AlertCircle className="w-6 h-6 text-amber-500" />
            ) : (
              <XCircle className="w-6 h-6 text-slate-400" />
            )}
            <div>
              <h2 className="text-lg font-heading font-semibold">Connection Status</h2>
              <p className="text-sm text-slate-600">
                {connected
                  ? `Connected via Microsoft OAuth ${subscriptionId ? `(Subscription: ${subscriptionId})` : ''}`
                  : configured
                  ? 'Microsoft configured - sign in to connect Azure'
                  : 'Not configured'}
              </p>
              {lastSync && (
                <p className="text-xs text-slate-500 mt-1">
                  Last synced: {new Date(lastSync).toLocaleString()}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex gap-2">
            {!configured ? (
              <Button
                onClick={() => window.location.href = '/m365-settings'}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                Configure Microsoft
              </Button>
            ) : !connected ? (
              <Button
                onClick={() => window.location.href = '/m365-integration'}
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
              >
                Sign in with Microsoft
              </Button>
            ) : (
              <Button
                onClick={handleDisconnect}
                variant="outline"
                className="rounded-sm text-red-600 hover:bg-red-50"
              >
                Clear History
              </Button>
            )}
          </div>
        </div>

        {!configured && (
          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-slate-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-700">
                <p className="font-medium mb-1">Configuration Required</p>
                <p>
                  Azure Defender for Cloud requires Microsoft OAuth authentication. 
                  Please configure your Microsoft 365 integration first, then sign in with a Microsoft account 
                  that has access to your Azure subscription.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Defender for Cloud Data Display */}
      {connected && securityData && (
        <div className="border border-border rounded-sm bg-white p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading font-semibold">Microsoft Defender for Cloud Security Posture</h2>
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync Controls'}
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Secure Score</p>
              <p className="text-3xl font-heading font-semibold text-green-600">
                {securityData.secure_score}%
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Total Assessments</p>
              <p className="text-3xl font-heading font-semibold text-slate-900">
                {securityData.total_assessments}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Healthy</p>
              <p className="text-3xl font-heading font-semibold text-green-600">
                {securityData.healthy_resources}
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-sm">
              <p className="text-sm font-medium text-slate-600 mb-1">Unhealthy</p>
              <p className="text-3xl font-heading font-semibold text-red-600">
                {securityData.unhealthy_resources}
              </p>
            </div>
          </div>

          {/* Recommendations by Severity */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-3">Recommendations by Severity</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-red-50 rounded-sm border border-red-100">
                <p className="text-sm text-red-700">Critical</p>
                <p className="text-2xl font-heading font-semibold text-red-600">
                  {securityData.critical_recommendations}
                </p>
              </div>
              <div className="p-3 bg-orange-50 rounded-sm border border-orange-100">
                <p className="text-sm text-orange-700">High</p>
                <p className="text-2xl font-heading font-semibold text-orange-600">
                  {securityData.high_recommendations}
                </p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-sm border border-yellow-100">
                <p className="text-sm text-yellow-700">Medium</p>
                <p className="text-2xl font-heading font-semibold text-yellow-600">
                  {securityData.medium_recommendations}
                </p>
              </div>
            </div>
          </div>

          {/* Regulatory Compliance Standards */}
          {securityData.regulatory_compliance && securityData.regulatory_compliance.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Regulatory Compliance</h3>
              <div className="space-y-2">
                {securityData.regulatory_compliance.map((standard, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-sm">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-slate-600" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{standard.standard}</p>
                        <p className="text-xs text-slate-600">
                          {standard.controls_passed} passed, {standard.controls_failed} failed
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-green-600">
                        {standard.compliance_percentage}%
                      </p>
                      <p className="text-xs text-slate-600">Compliance</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample Recommendations */}
          {securityData.recommendations && securityData.recommendations.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-900 mb-3">Sample Recommendations</h3>
              <div className="space-y-2">
                {securityData.recommendations.slice(0, 5).map((recommendation, index) => (
                  <div key={index} className="p-3 border border-border rounded-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            recommendation.status === 'Healthy' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {recommendation.status}
                          </span>
                          <span className={`text-xs px-2 py-0.5 rounded-sm ${
                            recommendation.severity === 'High' 
                              ? 'bg-red-100 text-red-800' 
                              : recommendation.severity === 'Medium'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {recommendation.severity}
                          </span>
                        </div>
                        <p className="text-sm text-slate-900">{recommendation.title}</p>
                        <p className="text-xs text-slate-600 mt-1">
                          {recommendation.category} • {recommendation.affected_resources} affected resources
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="border border-border rounded-sm bg-white p-6">
        <h2 className="text-lg font-heading font-semibold mb-4">How It Works</h2>
        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              1
            </div>
            <div>
              <p className="font-medium">Configure Microsoft 365 integration</p>
              <p className="text-slate-500 text-xs">Azure Defender uses the same OAuth as M365</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              2
            </div>
            <div>
              <p className="font-medium">Sign in with a Microsoft account that has Azure access</p>
              <p className="text-slate-500 text-xs">The account should have Reader access to your Azure subscription</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              3
            </div>
            <div>
              <p className="font-medium">We fetch security posture from Defender for Cloud</p>
              <p className="text-slate-500 text-xs">Secure Score, recommendations, and regulatory compliance data</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs">
              4
            </div>
            <div>
              <p className="font-medium">Defender recommendations are mapped to compliance controls</p>
              <p className="text-slate-500 text-xs">Your ISO 27001 and SOC 2 controls are automatically updated</p>
            </div>
          </div>
        </div>
      </div>

      {/* Required Permissions */}
      <div className="bg-slate-50 border border-slate-200 rounded-sm p-6">
        <h3 className="font-semibold text-slate-900 mb-3">Required Azure Permissions</h3>
        <p className="text-sm text-slate-700 mb-3">
          The Microsoft account used for authentication needs the following permissions in Azure:
        </p>
        <ul className="list-disc ml-5 space-y-1 text-sm text-slate-700">
          <li><strong>Reader</strong> role on the Azure subscription</li>
          <li><strong>Security Reader</strong> role for Defender for Cloud access</li>
          <li>Access to <strong>Microsoft.Security</strong> resource provider</li>
        </ul>
        <p className="text-xs text-slate-500 mt-3">
          These permissions are typically assigned through Azure Role-Based Access Control (RBAC).
        </p>
      </div>
    </div>
  );
}
